/**
 * ThemeSwitcher
 *
 * Dropdown and extended grid for selecting theme presets.
 */

import { useState, useEffect, useRef } from 'react'
import { FiCheck } from 'react-icons/fi'
import { LuPalette } from 'react-icons/lu'
import { useThemeConfig } from '../../theme'
import { themePresets, type ThemePreset } from '../../theme/presets'
import { cn } from '../../lib/utils'

interface ThemeSwitcherProps {
  extended?: boolean
  /** When true (default), dropdown opens upward. Set false for top-of-page contexts. */
  dropUp?: boolean
}

function SwatchDots({ preset, size = 12 }: { preset: ThemePreset; size?: number }) {
  return (
    <div className="flex gap-1 items-center">
      {preset.swatches.map((color, i) => (
        <div
          key={i}
          className={cn(
            'rounded-full border',
            preset.isDark ? 'border-white/30' : 'border-black/20'
          )}
          style={{ width: `${size}px`, height: `${size}px`, backgroundColor: color }}
        />
      ))}
    </div>
  )
}

function matchesPreset(currentColors: ThemePreset['config']['colors'], preset: ThemePreset): boolean {
  return currentColors.primary === preset.config.colors.primary
    && currentColors.accent === preset.config.colors.accent
    && currentColors.background === preset.config.colors.background
}

export function ThemeSwitcher({ extended = false, dropUp = true }: ThemeSwitcherProps) {
  const { config, updateConfig } = useThemeConfig()
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const activePreset = themePresets.find(p => matchesPreset(config.colors, p))

  useEffect(() => {
    if (!isOpen) return
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  const handleSelect = async (preset: ThemePreset) => {
    await updateConfig(preset.config)
    localStorage.setItem('docurious-theme-preset', preset.id)
    setIsOpen(false)
  }

  if (extended) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        {themePresets.map((preset) => {
          const isActive = activePreset?.id === preset.id
          return (
            <button
              key={preset.id}
              className={cn(
                'p-4 rounded-[var(--radius-card)] border-2 cursor-pointer transition-all duration-200 text-left hover:border-primary hover:shadow-md',
                isActive
                  ? 'border-primary bg-background-secondary'
                  : 'border-border bg-background'
              )}
              onClick={() => handleSelect(preset)}
            >
              <div className="flex justify-between items-center mb-3">
                <SwatchDots preset={preset} size={16} />
                {isActive && <FiCheck size={18} className="text-primary" />}
              </div>
              <p className="font-semibold text-sm text-foreground mb-1">
                {preset.name}
              </p>
              <p className="text-xs text-foreground-muted">
                {preset.description}
              </p>
            </button>
          )
        })}
      </div>
    )
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        className="flex items-center gap-2 px-3 py-2 rounded-[var(--radius-card)] cursor-pointer transition-all duration-200 hover:bg-background-secondary"
        onClick={() => setIsOpen(!isOpen)}
      >
        <LuPalette size={18} />
        <span className="text-sm font-medium">
          {activePreset?.name ?? 'Theme'}
        </span>
      </button>

      {isOpen && (
        <div className={cn(
          'absolute left-0 py-2 bg-background rounded-[var(--radius-card)] border border-border shadow-lg z-50 min-w-[200px]',
          dropUp ? 'bottom-full mb-2' : 'top-full mt-2'
        )}>
          <p className="px-3 py-2 text-xs font-semibold text-foreground-muted uppercase">
            Theme
          </p>

          {themePresets.map((preset) => {
            const isActive = activePreset?.id === preset.id
            return (
              <button
                key={preset.id}
                className={cn(
                  'flex w-full items-center gap-3 px-3 py-2 cursor-pointer hover:bg-background-secondary',
                  isActive && 'bg-background-secondary'
                )}
                onClick={() => handleSelect(preset)}
              >
                <SwatchDots preset={preset} />
                <span className="flex-1 text-sm text-left text-foreground">
                  {preset.name}
                </span>
                {isActive && <FiCheck size={16} className="text-primary" />}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
